import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { MatSelectModule } from '@angular/material/select'
import {
  MatSlideToggleModule,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle'
import { RouterModule } from '@angular/router'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { firstValueFrom, Subject, debounceTime } from 'rxjs'
import { Dialog } from '@angular/cdk/dialog'
import { ManageConnectionsDialogComponent } from './manage-connections-dialog/manage-connections-dialog.component'

import { Company, CompanyFilterValues } from '../../../types/company'
import { CompanyService } from '../../../services/company.service'
import { DialogService } from '../../../services/dialog.service'
import { TranslationService } from '../../../services/translation.service'
import { MatDialog } from '@angular/material/dialog'
import { ManageConnectionsDialogData } from '../../../types/companyConnection'
import { PaginatorIntl } from '../../intl/paginator-intl'

@Component({
  selector: 'app-company-list',
  imports: [
    MatInputModule,
    MatFormField,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatIcon,
    MatSelectModule,
    MatSlideToggleModule,
    RouterModule,
  ],
  templateUrl: './company-list.component.html',
  styleUrl: './company-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
})
export class CompanyListComponent implements OnInit {
  translationService = inject(TranslationService)
  private companyService = inject(CompanyService)
  private dialogService = inject(DialogService)
  dialog = inject(Dialog)
  matDialog = inject(MatDialog)
  private destroyRef = inject(DestroyRef)
  private nameChanges = new Subject<void>()
  private requestNumber = 0

  displayedColumns: string[] = ['name', 'visibility', 'actions']
  dataSource = new MatTableDataSource<Company>([])

  totalRecords = 0
  pageIndex = 0
  pageSize = 10
  savingCompanyId: string | null = null

  filterValues: CompanyFilterValues = { name: null, visibility: 'all' }

  constructor() {
    this.nameChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.filterChange())
  }

  async ngOnInit(): Promise<void> {
    await this.fetchCompanies(1)
  }

  async fetchCompanies(page: number): Promise<void> {
    const requestNumber = ++this.requestNumber
    const name = this.filterValues.name?.trim() || ''

    try {
      const res = await firstValueFrom(
        this.companyService.filterCompanies({
          page,
          name,
          visibility: this.filterValues.visibility,
          pageSize: this.pageSize,
        }),
      )
      if (requestNumber !== this.requestNumber) return
      this.totalRecords = res?.totalRecords ?? 0
      this.dataSource.data = res?.results ?? []
      if (
        page > 1 &&
        this.dataSource.data.length === 0 &&
        this.totalRecords > 0
      ) {
        this.pageIndex = page - 2
        await this.fetchCompanies(page - 1)
      }
    } catch {
      if (requestNumber !== this.requestNumber) return
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getErrorText(),
        this.translationService.getFailedToLoadCompaniesText(),
      )
    }
  }

  resetFilter() {
    this.filterValues = { name: null, visibility: 'all' }
    this.pageIndex = 0
    void this.fetchCompanies(1)
  }

  queueNameFilter(): void {
    this.nameChanges.next()
  }

  filterChange() {
    const trimmed = this.filterValues.name?.trim() ?? ''
    this.filterValues = {
      name: trimmed === '' ? null : trimmed,
      visibility: this.filterValues.visibility,
    }

    this.pageIndex = 0
    void this.fetchCompanies(1)
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize
    void this.fetchCompanies(this.pageIndex + 1)
  }

  async setMarketplacePublication(
    company: Company,
    event: MatSlideToggleChange,
  ): Promise<void> {
    const requestedPublication = event.checked
    event.source.checked = company.isMarketplacePublic
    this.savingCompanyId = company.id
    try {
      const response = await firstValueFrom(
        this.companyService.setMarketplacePublication(
          company.id,
          requestedPublication,
        ),
      )
      company.isMarketplacePublic = response.isMarketplacePublic
      event.source.checked = response.isMarketplacePublic
      if (this.filterValues.visibility !== 'all') {
        await this.fetchCompanies(this.pageIndex + 1)
      }
    } catch {
      event.source.checked = company.isMarketplacePublic
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getErrorText(),
        this.translationService.getMarketplaceSaveFailedText(),
      )
    } finally {
      this.savingCompanyId = null
    }
  }

  async toggleCompanyActive(companyId: string): Promise<void> {
    try {
      await this.companyService.toggleCompanyActive(companyId).toPromise()

      const rows = this.dataSource.data
      const index = rows.findIndex((c) => c.id === companyId)
      if (index !== -1) {
        rows[index].is_active = !rows[index].is_active
        this.dataSource.data = [...rows]
      }

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getCompaniesText(),
        this.translationService.getCompanyStatusChangedText(),
      )
    } catch {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getErrorText(),
        this.translationService.getFailedToToggleCompanyStatusText(),
      )
    }
  }

  manageConnections(company: Company): void {
    const data: ManageConnectionsDialogData = {
      companyId: company.id,
      companyName: company.name,
    }

    this.matDialog.open(ManageConnectionsDialogComponent, {
      data,
      maxWidth: '900px',
      width: '100%',
    })
  }
}
